import type { ReactNode } from "react";
import { cn } from "../lib/utils";

/**
 * - `default` : hauteur fixe 375px (portail client, grilles homogenes, description extensible).
 * - `catalog` : hauteur au contenu (back-office catalogue pro, sans vide entre texte et pied).
 */
export type ServicePresentationCardVariant = "default" | "catalog";

export interface ServicePresentationCardProps {
  className?: string;
  mediaClassName?: string;
  contentClassName?: string;
  label: ReactNode;
  description?: ReactNode;
  footerLeft: ReactNode;
  footerRight: ReactNode;
  media: ReactNode;
  mediaOverlayTop?: ReactNode;
  onClick?: () => void;
  ariaLabel?: string;
  disabled?: boolean;
  maxWidthPx?: number;
  variant?: ServicePresentationCardVariant;
}

/**
 * Carte de presentation de prestation (client + pro).
 * Meme composant : regler `variant` selon le contexte (`default` | `catalog`).
 */
export const ServicePresentationCard = ({
  className,
  mediaClassName,
  contentClassName,
  label,
  description,
  footerLeft,
  footerRight,
  media,
  mediaOverlayTop,
  onClick,
  ariaLabel,
  disabled = false,
  maxWidthPx,
  variant = "default",
}: Readonly<ServicePresentationCardProps>) => {
  const isCatalog = variant === "catalog";

  return (
    <div
      className={cn(
        "border-border/60 bg-card hover:border-primary/25 group relative mx-auto flex w-full min-w-0 flex-col overflow-hidden rounded-2xl border shadow-sm transition-[border-color,box-shadow,background-color] hover:shadow-md",
        isCatalog ? "h-auto max-h-none" : "h-[375px] max-h-[375px]",
        className,
      )}
      style={maxWidthPx ? { maxWidth: maxWidthPx } : undefined}
    >
      {onClick ? (
        <button
          type="button"
          aria-label={ariaLabel}
          disabled={disabled}
          className="focus-visible:ring-ring absolute inset-0 z-0 cursor-pointer rounded-2xl border-0 bg-transparent p-0 transition-colors focus-visible:outline-none focus-visible:ring-2 disabled:cursor-not-allowed"
          onClick={onClick}
        />
      ) : null}

      <div
        className={cn(
          "pointer-events-none relative z-10 flex min-w-0 cursor-default flex-col",
          !isCatalog && "h-full min-h-0 flex-1",
        )}
      >
        <div
          className={cn(
            "from-primary/15 via-muted/80 to-background bg-linear-to-b relative h-[168px] w-full shrink-0 overflow-hidden",
            mediaClassName,
          )}
        >
          {media}
          {mediaOverlayTop ? (
            <div className="absolute inset-x-3 top-3 z-10 flex items-start justify-between gap-2">
              {mediaOverlayTop}
            </div>
          ) : null}
        </div>

        <div
          className={cn(
            "flex min-w-0 flex-col gap-2 p-3 sm:gap-3 sm:p-4",
            !isCatalog && "min-h-0 flex-1",
            contentClassName,
          )}
        >
          <p className="text-foreground line-clamp-3 shrink-0 break-words text-[15px] font-semibold leading-snug tracking-tight sm:text-base">
            {label}
          </p>

          {description ? (
            <div
              className={cn(
                "min-h-0 overflow-hidden",
                isCatalog ? "shrink-0" : "flex-1",
              )}
            >
              {description}
            </div>
          ) : null}

          <div
            className={cn(
              "border-border/50 flex shrink-0 flex-wrap items-end justify-between gap-2 border-t pt-2.5",
              !isCatalog && "mt-auto",
            )}
          >
            {footerLeft}
            {footerRight}
          </div>
        </div>
      </div>
    </div>
  );
};
